/**
 * Regras de venda que o número do resumo (KPI) e as listas da tela Vendas dividem: o que está
 * "aberto agora", a base do Pix pago e a da aprovação de cartão. Uma regra só, num lugar só, para o
 * cartão e a lista sempre baterem (no modo ao vivo e na demonstração).
 *
 * Só contam e agrupam pedidos que o provider já entregou (nenhuma fórmula do `@rota/metrics`).
 */
import type { Order, Query } from '@rota/contracts';
import { toDateKeySP } from './time';

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

/** Boleto pendente conta como aberto por 3 dias. */
export const BOLETO_OPEN_MS = 3 * DAY;
/** Pix sem validade informada (servidor antigo): teto conservador de 24 h (nenhum pago passou disso). */
export const PIX_OPEN_FALLBACK_MS = DAY;
/** A aba Pendentes e o "Pendente agora" olham os pedidos criados nos últimos 4 dias. */
export const OPEN_LOOKBACK_MS = 4 * DAY;
/** Abaixo disto a aprovação de cartão não é julgada (uma recusa às 5 h não é alarme de adquirente). */
export const CARD_MIN_SAMPLE = 10;

/** O que o pedido do modo ao vivo traz além do contrato (forma não informada, carrinho abandonado). */
type LiveMarks = { paymentUnknown?: true; abandoned?: true };

export interface TimeWindow {
  from: number;
  to: number;
}

const inWin = (at: number | null | undefined, win: TimeWindow | undefined): boolean =>
  at != null && (!win || (at >= win.from && at <= win.to));

/** Pagamento aprovado (pago, ou reembolsado depois). */
export const isApprovedOrder = (o: Pick<Order, 'approvedAt' | 'status'>): boolean =>
  o.approvedAt != null && o.status !== 'refused';

/** Mesma pessoa em vários pedidos: o hash do comprador; sem ele, o pedido conta sozinho. */
export const buyerOf = (o: Pick<Order, 'id' | 'buyerKey' | 'customer'>): string =>
  o.buyerKey ?? o.customer?.emailMasked ?? o.id;

const unknownMethod = (o: object): boolean => (o as LiveMarks).paymentUnknown === true;

/**
 * Pix ou boleto ainda aberto no instante `at`, de qualquer dia: Pix até vencer (validade do servidor;
 * sem ela, 24 h), boleto por 3 dias. Carrinho abandonado e pedido sem forma de pagamento informada
 * não são dinheiro a receber.
 */
export function isOpenNow(
  o: Pick<Order, 'status' | 'createdAt' | 'paymentMethod' | 'pixExpiresAt'> & LiveMarks,
  at: number,
): boolean {
  if (o.status !== 'waiting_payment' || o.abandoned || o.paymentUnknown || o.createdAt > at) return false;
  if (o.paymentMethod === 'pix') return (o.pixExpiresAt ?? o.createdAt + PIX_OPEN_FALLBACK_MS) > at;
  if (o.paymentMethod === 'boleto') return at - o.createdAt <= BOLETO_OPEN_MS;
  return false;
}

/**
 * Pix gerado que venceu sem pagar (prazo do servidor): sai do "A receber" e da aba Pendentes, mas a
 * tela diz quantos ficaram de fora (a soma continua batendo). Carrinho abandonado não conta.
 */
export function isExpiredPix(
  o: Pick<Order, 'status' | 'createdAt' | 'paymentMethod' | 'pixExpiresAt'> & LiveMarks,
  at: number,
): boolean {
  return (
    o.status === 'waiting_payment' &&
    !o.abandoned &&
    o.paymentMethod === 'pix' &&
    o.pixExpiresAt != null &&
    o.pixExpiresAt <= at
  );
}

/** O período chega até hoje? Então "pendente" é o estoque aberto agora, não o do período. */
export function periodIncludesNow(q: Pick<Query, 'range'>, at: number): boolean {
  return q.range.to >= toDateKeySP(at);
}

export interface CardStats {
  /** Tentativas de cartão do curso (aprovadas + recusadas) criadas na janela. */
  attempts: number;
  approved: number;
  /** Compradores distintos que tentaram o cartão e quantos deles tiveram ao menos um aprovado. */
  buyers: number;
  approvedBuyers: number;
}

/** Aprovação de cartão do curso (por tentativa e por comprador), pela data de criação. */
export function courseCardStats(orders: readonly (Order & LiveMarks)[], win?: TimeWindow): CardStats {
  let attempts = 0;
  let approved = 0;
  const buyers = new Map<string, boolean>();
  for (const o of orders) {
    if (o.product !== 'front' || o.paymentMethod !== 'credit_card' || unknownMethod(o)) continue;
    if (!inWin(o.createdAt, win)) continue;
    const ok = isApprovedOrder(o);
    if (!ok && o.status !== 'refused') continue;
    attempts += 1;
    if (ok) approved += 1;
    const b = buyerOf(o);
    buyers.set(b, (buyers.get(b) ?? false) || ok);
  }
  let approvedBuyers = 0;
  for (const ok of buyers.values()) if (ok) approvedBuyers += 1;
  return { attempts, approved, buyers: buyers.size, approvedBuyers };
}

/**
 * Pix do curso gerados na janela (pela data de criação, inclusive os vencidos) e quantos foram pagos.
 * É a base do número "Pix pago" e da frase "X pagos de Y gerados".
 */
export function coursePixStats(
  orders: readonly (Order & LiveMarks)[],
  win?: TimeWindow,
): { generated: number; paid: number } {
  let generated = 0;
  let paid = 0;
  for (const o of orders) {
    if (o.product !== 'front' || o.paymentMethod !== 'pix' || unknownMethod(o) || o.abandoned) continue;
    if (!inWin(o.createdAt, win)) continue;
    generated += 1;
    if (isApprovedOrder(o)) paid += 1;
  }
  return { generated, paid };
}
